<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { AxiosError } from 'axios'

import SupportService from '@/services/support/supportService.ts'
import UserService from '@/services/user/userService.ts'
import { useAuthStore } from '@/stores/authStore.ts'
import { displayResponseError, displayToast } from '@/utils/toastUtils.ts'

import type {
  SupportTicketListItem,
  TicketPriority,
  TicketStatus,
  TicketType,
} from '@/types/services/support/support.type.ts'
import type { ApiError } from '@/types/services/utils/axios.type.ts'
import type { SortItem, SortOrder } from '@/types/services/utils/request.type.ts'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const tickets = ref<SupportTicketListItem[]>([])
const loading = ref<boolean>(false)
const search = ref<string>('')
const statusFilter = ref<TicketStatus | null>(null)
const sort = ref<string>('updated_at')
const order = ref<SortOrder>('desc')
const limit = ref<number>(10)
const page = ref<number>(1)
const total = ref<number>(0)
const maxPages = ref<number>(1)

const statusOptions = ['open', 'in_progress', 'resolved', 'closed'] as const
const typeOptions = ['question', 'problem', 'incident'] as const
const priorityOptions = ['low', 'normal', 'high', 'urgent'] as const

const statusColors: Record<TicketStatus, string> = {
  open: 'info',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'grey',
}
const priorityColors: Record<TicketPriority, string> = {
  low: 'grey',
  normal: 'info',
  high: 'warning',
  urgent: 'error',
}

const headers = computed(() => [
  { title: t('views.support.list.headers.subject'), key: 'subject', sortable: true },
  { title: t('views.support.list.headers.client'), key: 'client', sortable: false },
  { title: t('views.support.list.headers.status'), key: 'status', sortable: true },
  { title: t('views.support.list.headers.priority'), key: 'priority', sortable: true },
  { title: t('views.support.list.headers.assignee'), key: 'assignee', sortable: false },
  { title: t('views.support.list.headers.updatedAt'), key: 'updatedAt', sortable: true },
])

const statusSelectItems = computed(() => [
  { title: t('views.support.list.allStatuses'), value: null },
  ...statusOptions.map((value) => ({ title: t(`views.support.status.${value}`), value })),
])

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission)
}

function userLabel(user: { firstName: string | null; lastName: string | null; email: string }): string {
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  return name || user.email
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

async function fetchTickets(): Promise<void> {
  loading.value = true
  try {
    const response = await SupportService.getPaginated(
      { search: search.value, sort: sort.value, order: order.value, limit: limit.value, page: page.value },
      statusFilter.value,
    )
    tickets.value = response.data
    total.value = response.total
    maxPages.value = response.maxPages
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>)
  } finally {
    loading.value = false
  }
}

watch([search, statusFilter, sort, order, limit, page], fetchTickets, { immediate: true })

function onSort(sortBy: SortItem<string>[]): void {
  if (sortBy.length) {
    sort.value = sortBy[0].key
    order.value = sortBy[0].order
  } else {
    sort.value = 'updated_at'
    order.value = 'desc'
  }
}

function openTicket(item: SupportTicketListItem): void {
  router.push({ name: 'supportTicketDetail', params: { ticketId: String(item.id) } })
}

function onRowClick(_event: unknown, row: { item: SupportTicketListItem }): void {
  openTicket(row.item)
}

// --- Create ticket dialog ---
const createDialog = ref<boolean>(false)
const createLoading = ref<boolean>(false)
const clientSearch = ref<string>('')
const clientItems = ref<{ title: string; value: number }[]>([])
const clientLoading = ref<boolean>(false)
const form = ref<{
  clientId: number | null
  subject: string
  type: TicketType
  priority: TicketPriority
  content: string
}>({ clientId: null, subject: '', type: 'question', priority: 'normal', content: '' })

// The currently selected client item, kept so its title always resolves even after
// the search list is reloaded (otherwise the autocomplete falls back to showing the raw id).
const selectedClientItem = ref<{ title: string; value: number } | null>(null)

const canSubmit = computed(
  () => !!form.value.clientId && form.value.subject.trim() !== '' && form.value.content.trim() !== '',
)

async function loadClients(term: string): Promise<void> {
  clientLoading.value = true
  try {
    const response = await UserService.getPaginatedUser({
      search: term ?? '', sort: 'email', order: 'asc', limit: 10, page: 1,
    })
    const items = response.data.map((user) => ({
      title: `${userLabel(user)} — ${user.email}`,
      value: user.id,
    }))
    // Always keep the selected client in the list so its name stays displayed.
    if (selectedClientItem.value && !items.some((item) => item.value === selectedClientItem.value!.value)) {
      items.unshift(selectedClientItem.value)
    }
    clientItems.value = items
  } catch (error) {
    clientItems.value = selectedClientItem.value ? [selectedClientItem.value] : []
    displayResponseError(error as AxiosError<ApiError>)
  } finally {
    clientLoading.value = false
  }
}

function onClientSelected(value: number | null): void {
  selectedClientItem.value = clientItems.value.find((item) => item.value === value) ?? null
}

let clientSearchTimer: ReturnType<typeof setTimeout> | undefined
watch(clientSearch, (value) => {
  // Skip the refetch triggered when selecting an item sets the search to its title.
  if (selectedClientItem.value && value === selectedClientItem.value.title) return
  if (clientSearchTimer) clearTimeout(clientSearchTimer)
  clientSearchTimer = setTimeout(() => loadClients(value), 300)
})

function openCreate(): void {
  form.value = { clientId: null, subject: '', type: 'question', priority: 'normal', content: '' }
  clientItems.value = []
  selectedClientItem.value = null
  createDialog.value = true
  void loadClients('')
}

async function submitCreate(): Promise<void> {
  if (!canSubmit.value || !form.value.clientId) return
  createLoading.value = true
  try {
    await SupportService.create({
      clientId: form.value.clientId,
      subject: form.value.subject,
      content: form.value.content,
      type: form.value.type,
      priority: form.value.priority,
    })
    displayToast(t('views.support.toast.created'), 'success')
    createDialog.value = false
    await fetchTickets()
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>)
  } finally {
    createLoading.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap ga-3">
      <h1 class="text-h5 font-weight-bold">{{ t('views.support.list.title') }}</h1>
      <v-btn
        v-if="hasPermission(['CREATE_SUPPORT_TICKET'])"
        color="secondary"
        prepend-icon="PhPlus"
        @click="openCreate"
      >
        {{ t('views.support.list.newTicket') }}
      </v-btn>
    </div>

    <div class="d-flex ga-3 mb-4 flex-wrap">
      <v-text-field
        v-model="search"
        :label="t('views.support.list.search')"
        density="compact"
        variant="outlined"
        prepend-inner-icon="PhMagnifyingGlass"
        hide-details
        clearable
        style="max-width: 320px"
      />
      <v-select
        v-model="statusFilter"
        :items="statusSelectItems"
        density="compact"
        variant="outlined"
        hide-details
        style="max-width: 220px"
      />
    </div>

    <v-data-table-server
      :headers="headers"
      :items="tickets"
      :items-length="total"
      :loading="loading"
      :items-per-page="limit"
      :page="page"
      hover
      @update:sort-by="onSort"
      @click:row="onRowClick"
    >
      <template #[`item.client`]="{ item }">
        {{ userLabel(item.client) }}
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip :color="statusColors[item.status]" size="small" label>
          {{ t(`views.support.status.${item.status}`) }}
        </v-chip>
      </template>
      <template #[`item.priority`]="{ item }">
        <v-chip :color="priorityColors[item.priority]" size="small" label variant="tonal">
          {{ t(`views.support.priority.${item.priority}`) }}
        </v-chip>
      </template>
      <template #[`item.assignee`]="{ item }">
        {{ item.assignee ? userLabel(item.assignee) : t('views.support.list.unassigned') }}
      </template>
      <template #[`item.updatedAt`]="{ item }">
        {{ formatDate(item.updatedAt) }}
      </template>
      <template #no-data>
        {{ t('views.support.list.empty') }}
      </template>
      <template #bottom>
        <div class="d-flex justify-center pa-3">
          <v-pagination v-model="page" :length="maxPages" :total-visible="6" density="comfortable" />
        </div>
      </template>
    </v-data-table-server>

    <!-- Create ticket dialog -->
    <v-dialog v-model="createDialog" max-width="560">
      <v-card>
        <v-card-title>{{ t('views.support.create.title') }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="form.clientId"
            v-model:search="clientSearch"
            :items="clientItems"
            :loading="clientLoading"
            :label="t('views.support.create.clientSearch')"
            variant="outlined"
            density="comfortable"
            no-filter
            class="mb-2"
            @update:model-value="onClientSelected"
          />
          <v-text-field
            v-model="form.subject"
            :label="t('views.support.create.subject')"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          />
          <div class="d-flex ga-3">
            <v-select
              v-model="form.type"
              :items="typeOptions.map((value) => ({ title: t(`views.support.type.${value}`), value }))"
              :label="t('views.support.create.type')"
              variant="outlined"
              density="comfortable"
              class="mb-2"
            />
            <v-select
              v-model="form.priority"
              :items="priorityOptions.map((value) => ({ title: t(`views.support.priority.${value}`), value }))"
              :label="t('views.support.create.priority')"
              variant="outlined"
              density="comfortable"
              class="mb-2"
            />
          </div>
          <v-textarea
            v-model="form.content"
            :label="t('views.support.create.message')"
            variant="outlined"
            rows="4"
            auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="createDialog = false">{{ t('views.support.create.cancel') }}</v-btn>
          <v-btn color="secondary" :disabled="!canSubmit" :loading="createLoading" @click="submitCreate">
            {{ t('views.support.create.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
